import React from 'react'
import {
  Body2,
  Body3,
  Bold,
  Box,
  Color,
  Display,
  FlexDirection,
  Heading2,
  JustifyContent,
  TextAlign,
} from '@gousto-internal/citrus-react'

import { ContentstackImage } from '../ContentstackImage'
import { CtaLink, mapCmsCta } from '../../cta'
import type { CmsCtaIntentHandlers, OnTriggerPromoCode } from '../../cta'
import { CmsCarousel } from './CmsCarousel'
import type { CarouselFields } from './types'

export type CarouselItemFields = CarouselFields['items'][number]

type CarouselProps = CarouselFields & {
  carouselAreaLabel?: string
  ctaIntentHandlers?: CmsCtaIntentHandlers
  onTriggerPromocode?: OnTriggerPromoCode
}

const CarouselCard = ({
  item,
  ctaIntentHandlers,
  onTriggerPromocode,
}: {
  item: CarouselItemFields
  ctaIntentHandlers?: CmsCtaIntentHandlers
  onTriggerPromocode?: OnTriggerPromoCode
}) => {
  const cta = mapCmsCta(item.cta, ctaIntentHandlers, onTriggerPromocode)

  return (
    <Box paddingH={[0, 2]} paddingBottom={[4, 0]} height="100%">
      <Box
        bg={Color.White}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        height="100%"
        paddingH={3}
        paddingV={3}
        style={{ borderRadius: '4px' }}
        width={['100%', '220px']}
      >
        <ContentstackImage
          alt={item.image_alt}
          fit="cover"
          height={['170px', '170px']}
          sizes="(max-width: 767px) 100vw, 220px"
          src={item.image.url}
          style={{ borderRadius: '4px' }}
          width={['100%', '100%']}
        />

        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          flexGrow={1}
          justifyContent={JustifyContent.SpaceBetween}
          paddingH={2}
          paddingV={2}
        >
          <Box>
            <Box paddingBottom={1}>
              <Body2 size={4}>
                <Bold>{item.title}</Bold>
              </Body2>
            </Box>
            {item.description ? (
              <Body2 size={4} color={Color.ColdGrey_700}>
                {item.description}
              </Body2>
            ) : null}
          </Box>

          {cta?.href ? (
            <Box paddingV={2}>
              <Body3 size={2} color={Color.Secondary_400}>
                <Bold>
                  <CtaLink cta={cta} isLink />
                </Bold>
              </Body3>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  )
}

export const Carousel = ({
  heading,
  items,
  carouselAreaLabel = 'Food box options',
  ctaIntentHandlers,
  onTriggerPromocode,
}: CarouselProps) => (
  <Box bg={Color.NeutralGrey_50} paddingV={8}>
    <Box maxWidth="1200px" paddingH={[4, 6]} style={{ margin: '0 auto' }}>
      <Box paddingBottom={6}>
        <Heading2 textAlign={[TextAlign.Left, TextAlign.Center, TextAlign.Center]}>
          {heading}
        </Heading2>
      </Box>

      <Box display={[Display.Block, Display.None]}>
        {items.map((item) => (
          <CarouselCard
            item={item}
            key={item.item_id}
            ctaIntentHandlers={ctaIntentHandlers}
            onTriggerPromocode={onTriggerPromocode}
          />
        ))}
      </Box>
      <Box display={[Display.None, Display.Flex]} justifyContent={JustifyContent.Center}>
        <CmsCarousel carouselAreaLabel={carouselAreaLabel} options={{ loop: true }}>
          {items.map((item) => (
            <CarouselCard
              item={item}
              key={item.item_id}
              ctaIntentHandlers={ctaIntentHandlers}
              onTriggerPromocode={onTriggerPromocode}
            />
          ))}
        </CmsCarousel>
      </Box>
    </Box>
  </Box>
)
